"use client";

import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/format/date";
import {
  CheckCircle2,
  Clock,
  User,
  AlertCircle,
  ArrowRight,
  XCircle,
  Truck,
  MapPin,
  Ruler,
  FileCheck,
} from "lucide-react";

interface TimelineEntry {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  actor?: {
    id: string;
    publicId: string;
    name: string;
    role: string;
  } | null;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

interface TimelineProps {
  entries: TimelineEntry[];
  className?: string;
}

const statusIcons: Record<string, typeof Clock> = {
  DRAFT: Clock,
  CONFIRMED: CheckCircle2,
  ASSIGNED: User,
  EN_ROUTE: Truck,
  ARRIVED: MapPin,
  IN_PROGRESS: Ruler,
  MEASUREMENTS_SUBMITTED: FileCheck,
  PENDING_CUSTOMER_ACCEPTANCE: Clock,
  ACCEPTED: CheckCircle2,
  CORRECTION_REQUESTED: AlertCircle,
  CANCELLED: XCircle,
  CLOSED: CheckCircle2,
  RESCHEDULED: Clock,
  NO_SHOW_CUSTOMER: AlertCircle,
  NO_SHOW_EXEC: AlertCircle,
};

const statusColors: Record<string, string> = {
  DRAFT: "text-gray-500 bg-gray-100",
  CONFIRMED: "text-blue-600 bg-blue-100",
  ASSIGNED: "text-cyan-600 bg-cyan-100",
  EN_ROUTE: "text-orange-500 bg-orange-100",
  ARRIVED: "text-teal-600 bg-teal-100",
  IN_PROGRESS: "text-indigo-600 bg-indigo-100",
  MEASUREMENTS_SUBMITTED: "text-blue-600 bg-blue-100",
  PENDING_CUSTOMER_ACCEPTANCE: "text-amber-600 bg-amber-100",
  ACCEPTED: "text-emerald-600 bg-emerald-100",
  CORRECTION_REQUESTED: "text-orange-600 bg-orange-100",
  CANCELLED: "text-red-600 bg-red-100",
  CLOSED: "text-slate-600 bg-slate-100",
  RESCHEDULED: "text-purple-600 bg-purple-100",
  NO_SHOW_CUSTOMER: "text-red-500 bg-red-100",
  NO_SHOW_EXEC: "text-red-500 bg-red-100",
};

function formatStatus(status: string): string {
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function Timeline({ entries, className }: TimelineProps) {
  if (entries.length === 0) {
    return (
      <div className={cn("py-8 text-center text-muted-foreground", className)}>
        No timeline events yet.
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {entries.map((entry, index) => {
        const Icon = statusIcons[entry.toStatus] || Clock;
        const colorClass = statusColors[entry.toStatus] || "text-gray-500 bg-gray-100";
        const isLast = index === entries.length - 1;

        return (
          <div key={entry.id} className="relative flex gap-4">
            <div className="flex flex-col items-center">
              <div className={cn("p-2 rounded-full", colorClass)}>
                <Icon className="h-4 w-4" />
              </div>
              {!isLast && (
                <div className="w-px flex-1 bg-border mt-2" />
              )}
            </div>
            <div className={cn("pb-4", !isLast && "border-b border-dashed")}>
              <div className="flex items-center gap-2 mb-1">
                {entry.fromStatus && (
                  <>
                    <span className="text-xs text-muted-foreground">
                      {formatStatus(entry.fromStatus)}
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  </>
                )}
                <span className="font-medium text-sm">
                  {formatStatus(entry.toStatus)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{formatDateTime(entry.createdAt)}</span>
                {entry.actor && (
                  <>
                    <span>•</span>
                    <span>{entry.actor.name}</span>
                  </>
                )}
              </div>
              {entry.meta && typeof entry.meta === "object" && Object.keys(entry.meta).length > 0 && (
                <div className="mt-2 p-2 bg-muted rounded-md text-xs">
                  {Object.entries(entry.meta).map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                      <span className="font-medium capitalize">{key.replace(/_/g, " ")}:</span>
                      <span className="text-muted-foreground">{String(value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
